import { useEffect, useState } from 'react';

interface ConsultorsProps {
  onSelectConsultor: (consultor: string, name: string, types: string[]) => void;
}

interface ConsultorCardProps {
  name: string;
  slug: string;
  title: string;
  description: string;
  tags: string[];
  rawTypes?: string[];
  image?: string | null;
  pricePerMin?: number;
  onSelect: () => void;
}

interface PerformerApi {
  id?: string;
  slug?: string;
  name?: string;
  title?: string;
  description?: string;
  type?: string[];
  image?: string | null;
  pricePerMin?: number;
}

const slugifyName = (name: string) =>
  name
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '') || 'consultor';

const categoryLabels: Record<string, string> = {
  amor: 'Amor',
  relacoes: 'Relações',
  reconciliacao: 'Reconciliação',
  carreira: 'Carreira',
  negocios: 'Negócios',
  financas: 'Finanças',
  espiritualidade: 'Espiritualidade',
  proposito: 'Propósito',
  autoconhecimento: 'Autoconhecimento',
  decisoes: 'Decisões',
  planeamento: 'Planeamento',
  'estrategia-pessoal': 'Estratégia Pessoal',
};

const formatTag = (tag: string) =>
  categoryLabels[tag] || tag.charAt(0).toUpperCase() + tag.slice(1);

const categoryGradients: Record<string, string> = {
  amor: 'bg-gradient-to-br from-[rgba(248,113,113,0.25)] via-transparent to-transparent',
  relacoes: 'bg-gradient-to-br from-[rgba(248,250,252,0.1)] via-transparent to-transparent',
  reconciliacao: 'bg-gradient-to-br from-[rgba(251,191,36,0.25)] via-transparent to-transparent',
  carreira: 'bg-gradient-to-br from-[rgba(34,197,94,0.25)] via-transparent to-transparent',
  negocios: 'bg-gradient-to-br from-[rgba(20,184,166,0.25)] via-transparent to-transparent',
  financas: 'bg-gradient-to-br from-[rgba(234,179,8,0.25)] via-transparent to-transparent',
  espiritualidade: 'bg-gradient-to-br from-[rgba(59,130,246,0.25)] via-transparent to-transparent',
  proposito: 'bg-gradient-to-br from-[rgba(168,85,247,0.25)] via-transparent to-transparent',
  autoconhecimento: 'bg-gradient-to-br from-[rgba(236,72,153,0.25)] via-transparent to-transparent',
  decisoes: 'bg-gradient-to-br from-[rgba(99,102,241,0.25)] via-transparent to-transparent',
  planeamento: 'bg-gradient-to-br from-[rgba(14,165,233,0.25)] via-transparent to-transparent',
  'estrategia-pessoal': 'bg-gradient-to-br from-[rgba(217,70,239,0.25)] via-transparent to-transparent',
};

const formatPrice = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return `€ ${value.toFixed(2)}/min`;
};

function ConsultorCard({ name, title, description, tags, rawTypes, image, pricePerMin, onSelect }: ConsultorCardProps) {
  const firstRawType = rawTypes?.[0] || '';
  const gradientClass = categoryGradients[firstRawType] || '';

  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(image && !imageFailed);

  return (
    <article className="bg-[radial-gradient(circle_at_top_left,rgba(148,163,184,0.2),rgba(15,23,42,0.95))] rounded-[18px] p-5 border border-[rgba(148,163,184,0.35)] shadow-[0_16px_35px_rgba(15,23,42,0.7)]">
      <div className="mb-3.5">
        <div className={`relative w-full aspect-video rounded-[14px] overflow-hidden flex flex-col justify-end p-3 text-[#f9fafb] shadow-[0_18px_40px_rgba(15,23,42,0.9)] bg-[radial-gradient(circle_at_top_left,rgba(148,163,184,0.35),rgba(15,23,42,0.95))] ${gradientClass}`}>
          {showImage && (
            <img
              src={image || ''}
              alt={`Consultor ${name}`}
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[rgba(2,6,23,0.88)] via-[rgba(2,6,23,0.2)] to-transparent" />
          <span className="relative z-10 font-bold text-[1.05rem]">{name}</span>
          <span className="relative z-10 text-[0.8rem] opacity-90">{title}</span>
        </div>
      </div>
      <h3 className="mt-0 mb-2 text-lg font-bold text-[#e5e7eb]">{name}</h3>
      <p className="text-[0.9rem] text-[#e5e7eb] mb-1 font-semibold">{formatPrice(pricePerMin)}</p>
      <p className="text-[0.9rem] text-[#cbd5f5] mb-3">{description}</p>
      <ul className="list-none p-0 m-0 mb-4 flex flex-wrap gap-1.5">
        {tags.map((tag, index) => (
          <li
            key={index}
            className="text-[0.8rem] px-2 py-1 rounded-full bg-[rgba(15,23,42,0.85)] border border-[rgba(148,163,184,0.6)]"
          >
            {tag}
          </li>
        ))}
      </ul>
      <button
        onClick={onSelect}
        className="w-full inline-flex items-center justify-center rounded-full px-4 py-2 text-[0.95rem] font-medium bg-gradient-to-br from-[#8b5cf6] to-[#ec4899] text-[#f9fafb] shadow-[0_8px_20px_rgba(15,23,42,0.6)] hover:translate-y-[-1px] hover:brightness-105 transition-all"
      >
        Falar com {name.includes('o') ? 'o' : 'a'} {name}
      </button>
    </article>
  );
}

export default function Consultors({ onSelectConsultor }: ConsultorsProps) {
  const [consultores, setConsultores] = useState<ConsultorCardProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mapPerformerToCard = (p: PerformerApi): ConsultorCardProps => {
    const name = p.name || 'Consultor';
    const tags = Array.isArray(p.type) && p.type.length ? p.type.map(formatTag) : ['Disponível'];
    const slug = p.slug || (p.id ? slugifyName(p.id) : slugifyName(name));
    // console.log('Mapping performer:', p, 'to slug:', slug);
    const title = p.title || tags[0] || 'Consultor virtual';
    const description = p.description || 'Disponível para o ajudar com orientação personalizada.';
    const pricePerMin = typeof p.pricePerMin === 'number' ? p.pricePerMin : undefined;
    const image = typeof p.image === 'string' && p.image.trim() ? p.image.trim() : null;

    return {
      name,
      slug,
      title,
      description,
      tags,
      rawTypes: Array.isArray(p.type) ? p.type : [],
      image,
      pricePerMin,
      onSelect: () => onSelectConsultor(slug, name, tags)
    };
  };

  const fetchConsultores = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/performers/active');
      // console.log('Fetching performers from /api/performers/active');
      // console.log('Response status:', res.status);

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      const list = Array.isArray(data) ? data : data?.performers || [];
      setConsultores(list.map(mapPerformerToCard));
    } catch (err) {
      console.error('Erro ao carregar consultores ativos:', err);
      setError('Não foi possível carregar os consultores neste momento.');
      setConsultores([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsultores();
  }, []);

  return (
    <section id="consultores" className="py-[60px] bg-[radial-gradient(circle_at_top,rgba(15,23,42,0.9),#020617_55%)]">
      <div className="max-w-[1120px] mx-auto px-4">
        <h2 className="text-[1.8rem] mb-2 font-bold text-[#e5e7eb]">Conheça os consultores virtuais</h2>
        <p className="mt-0 mb-6 max-w-[640px] text-[#cbd5f5]">
          Cada consultor tem uma personalidade e abordagem própria, para que possa escolher
          quem melhor se adapta à sua energia e à sua questão.
        </p>

        {loading && (
          <div className="flex items-center gap-3 text-[#cbd5f5]">
            <div className="animate-spin h-6 w-6 rounded-full border-b-2 border-[#8b5cf6]"></div>
            <span>A carregar consultores...</span>
          </div>
        )}

        {error && (
          <div className="mt-4 bg-[rgba(127,29,29,0.15)] border border-[rgba(248,113,113,0.4)] text-[#fecdd3] rounded-lg p-4">
            <div className="flex items-center justify-between gap-3">
              <span>{error}</span>
              <button
                onClick={fetchConsultores}
                className="px-3 py-1.5 rounded-md bg-[#8b5cf6] text-white text-sm hover:brightness-110 transition"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        {!loading && !error && (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-6">
            {consultores.map((consultor) => (
              <ConsultorCard
                key={consultor.slug}
                {...consultor}
              />
            ))}
          </div>
        )}

        {!loading && !error && consultores.length === 0 && (
          <div className="mt-4 text-[#cbd5f5] bg-[rgba(15,23,42,0.85)] border border-[rgba(148,163,184,0.3)] rounded-lg p-4">
            Nenhum consultor disponível no momento.
          </div>
        )}
      </div>
    </section>
  );
}
